import { ComponentProps } from 'react';
import Component from '@cx-modules/ev-cards';
import EvCardsCss from '@cx-modules/ev-cards/style.css?inline';
import { widgetBootstrap } from '../../services/cx-module-helper';
import {
  Keys,
  SanitisedContractProps as SanitisedProps
} from '../../utils/contractProps/contractProps.js';
import css from '../../utils/css';
import { meta } from './meta.js';

export const attributes = meta.attributes;
export const widgetName = meta.name;

const { VITE_SPORTS_DATA_API_HOST: dataApiEndpoint } = import.meta.env;

type ContractProps = ComponentProps<typeof Component>;

type Bookmakers = ContractProps['bookmakers'];
type OddsFormat = ContractProps['oddsFormat'];
type Repub = ContractProps['repub'];
type Theme = ContractProps['theme'];
type AnalyticsDataLayer = ContractProps['analyticsDataLayer'];

type OmittedProps = Keys<
  ContractProps,
  // supplied by widgetBootstrap
  'dehydratedState' | 'i18n'
>;

type SanitisedContractProps = SanitisedProps<
  ContractProps,
  OmittedProps,
  never
>;

type ContractParams = {
  affIds?: Record<string, string>;
  apiKey: string;
  analytics?: { getDataLayer: () => AnalyticsDataLayer };
  bookmakers?: Bookmakers;
  campaignName?: string;
  categoryId?: number | string;
  categoryGroupId?: number;
  countryCode?: string;
  enableClickouts?: boolean;
  eventId?: number | string;
  limit?: string;
  marketId?: number | string;
  oddsFormat?: OddsFormat;
  repub?: Repub;
  showBookieLogos?: boolean;
  showDateAndTime?: boolean;
  showFixture?: boolean;
  showTrendsPips?: boolean;
  showTrendsRationale?: boolean;
  subdivisionCode?: string;
  subeventId?: number | string;
  widgetStyle?: string;
  hash?: string;
};

export const contract = ({
  affIds,
  apiKey,
  analytics,
  bookmakers,
  campaignName,
  categoryId,
  categoryGroupId,
  countryCode,
  enableClickouts,
  eventId,
  limit,
  marketId,
  oddsFormat,
  repub,
  showBookieLogos,
  showDateAndTime,
  showFixture,
  showTrendsPips,
  showTrendsRationale,
  subdivisionCode,
  subeventId,
  widgetStyle: theme,
  hash: widgetSlot
}: ContractParams) => {
  const props: SanitisedContractProps = {
    affIds,
    apiKey,
    dataApiEndpoint,
    disableAnalytics: !analytics,
    analyticsImpressionEventData: {
      bookieCode: Object.keys(bookmakers || {}),
      bookieName: Object.values(bookmakers || {}).map(b => b.bookmakerName),
      campaignName,
      widgetType: 'value-bet',
      widgetSlot,
      variation: 'value-bet-main-card',
      recommendedMarketId: '' // blank as requested by analytics
    },
    analyticsImpressionEventName: 'widget_impression',
    bookmakers,
    categoryId,
    categoryGroupId,
    countryCode: countryCode ?? 'US',
    enableClickouts,
    eventId,
    limit: limit ? parseInt(limit, 10) : undefined,
    marketId,
    oddsFormat,
    repub,
    showBookieLogos,
    showDateAndTime,
    showFixture,
    showTrendsPips,
    showTrendsRationale,
    subdivisionCode: undefined,
    subeventId,
    theme: theme as Theme
  };

  const countriesRequiringSubdivisionCode = ['US', 'CA'];
  const normalisedCountryCode = props.countryCode?.toUpperCase();

  if (analytics) {
    props.analyticsDataLayer = analytics.getDataLayer();
  }

  if (normalisedCountryCode) {
    props.countryCode = normalisedCountryCode;
  }

  if (
    normalisedCountryCode &&
    countriesRequiringSubdivisionCode.includes(normalisedCountryCode)
  ) {
    const normalisedSubdivisionCode = (subdivisionCode ?? 'NJ')?.toUpperCase();
    props.subdivisionCode = normalisedSubdivisionCode;
  }

  return props;
};

// Cache key matching getEvCardsCacheKey from cx-modules/ev-cards
const queryKey = (campaignData: SanitisedContractProps) => [
  'ev-cards',
  {
    bookmakerCodes: campaignData.bookmakers
      ? Object.keys(campaignData.bookmakers).join(',')
      : undefined,
    categoryId: campaignData.categoryId,
    eventId: campaignData.eventId,
    limit: campaignData.limit,
    marketId: campaignData.marketId,
    subeventId: campaignData.subeventId
  }
];

// Shadow DOM resets — cxy-components CSS targets `html` and `*` for base resets,
// which don't apply inside shadow DOM, breaking button styles and image sizing.
const customCss = `
  :host {
    text-rendering: optimizelegibility;
    -webkit-font-smoothing: antialiased;
    -moz-font-smoothing: antialiased;
  }

  *, *:before, *:after {
    box-sizing: inherit;
    font-family: var(--cxy-font-family-sans);
  }

  button {
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    color: inherit;
    font: inherit;
  }

  button img[alt] {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  button[class*='oddsButton'] {
    margin: 0;
    width: 100%;
  }

  button[class*='oddsButton'] [class*='oddsContainer'] [class*='line'],
  button[class*='oddsButton'] [class*='oddsContainer'] [class*='odds'] {
    display: flex;
    align-items: center;
  }
`;

widgetBootstrap({
  attributes,
  contract,
  Component,
  customCss,
  queryKey,
  style: EvCardsCss,
  themeContract: css`
    #root > div {
      --ev-cards-font-family-event: var(--widget-font-family);
      --cxy-font-family-sans: var(--widget-font-family);

      --muted-text: color-mix(
        in srgb,
        var(--widget-text-color),
        transparent 20%
      );
      --muted-accent-text: color-mix(
        in srgb,
        var(--widget-accent-text-color),
        transparent 20%
      );

      --cxy-color-red: rgb(
        255,
        67,
        90
      ); /* incorrectly used widget color values */
      --cxy-color-content-default: var(--widget-text-color);

      --ev-cards-main-bg: var(--widget-background-color);
      --ev-cards-footer-bg: var(--widget-accent-background-color);
      --ev-cards-header-bg: var(--widget-accent-background-color);
      --ev-cards-border: 1px solid var(--widget-accent-background-color);
      --ev-cards-header-text-color: var(--widget-accent-text-color);
      --ev-cards-highlight-bg: color-mix(
        in srgb,
        var(--widget-background-color),
        var(--widget-text-color) 10%
      );
      --ev-cards-odds-value-color: var(--widget-cta-color);
      --ev-cards-info-border-color: var(--muted-text);
      --ev-cards-info-color: var(--muted-text);
      --ev-cards-border-radius: var(--widget-border-radius, 12px);
      --ev-cards-odds-radius: var(--widget-cta-border-radius);

      --ev-cards-track-bg: #aaa3;

      --cxy-color-success: var(--widget-positive-color, #13df94);
      --ev-cards-warning: var(--widget-midway-color, #f5c451);
      --cxy-color-container-danger: var(--widget-negative-color, #ff435a);
      --ev-cards-accent: var(--cxy-color-success);

      [class*='_performanceContent_'] {
        --cxy-color-content-default: var(--widget-accent-text-color);
      }
      [class*='_cardHeader_'] {
        /* muted for header */
        --ev-cards-muted-text: var(--muted-accent-text);
        --ev-cards-info-border-color: var(--muted-accent-text);
        --ev-cards-info-color: var(--muted-accent-text);
      }

      [class*='_oddsButton_'] {
        --ev-cards-odds-bg: var(--widget-cta-background-color);
        color: var(
          --widget-cta-color
        ) !important; /* overriding another !important from the module */

        [class*='_logoContainer_'] {
          border-radius: var(--widget-cta-inner-border-radius, 36px);
        }

        &:hover {
          background-color: var(--widget-cta-hover-color) !important;
        }
      }

      /* flame bg */
      [class*='_typeBadgeWrapper_'] svg rect {
        fill: var(--ev-cards-main-bg);
      }
    }
  `,
  name: widgetName,
  version: '1.0.0' // NOTE: This value should match the last version from the widget's CHANGELOG.md file.
});
